/**
 * @file Audit-log action enum (CLIENT mirror).
 *
 * Every value written to `audit_log/{id}.action` MUST come from this module.
 * Bare strings would silently bypass the rule's allowlist check and break
 * the audit-log page's "filter by action" dropdown.
 *
 * The server-side counterpart is `functions/lib/audit-actions.js`. The two
 * files MUST stay byte-for-byte aligned (same keys, same values), AND every
 * value must appear in the `isKnownAuditAction` allowlist in
 * firestore.rules (an action missing there is silently rejected at write
 * time). `tests/invariants.test.js` enforces both invariants — `npm test`
 * fails loudly if any of the three drifts. When you add an action: add it
 * here, in the server mirror, and in the rules allowlist.
 *
 * Naming convention: `domain.verb_or_phrase`. Keep the domain prefix stable
 * so the audit-log filter UI can group actions by domain in the dropdown.
 */

export const AUDIT_ACTION = Object.freeze({
  // ── Auth ────────────────────────────────────────────────────────────────
  /** Successful @strv.com sign-in. Emitted once per browser tab — guarded
   *  by sessionStorage so refreshes don't spam the log. */
  AUTH_SIGNIN:       'auth.signin',

  // ── Onboarding ──────────────────────────────────────────────────────────
  /** Per-item status transition. `details = { itemId, name, category, from, to }`. */
  ONBOARDING_STATUS: 'onboarding.item_status',
  /** Joining-form submission matched to an employee (manual). */
  ONBOARDING_FORM_MATCH:  'onboarding.form_match',
  /** Joining-form submission dismissed. */
  ONBOARDING_FORM_IGNORE: 'onboarding.form_ignore',
  /** Admin finished onboarding without handing anything over (e.g. the
   *  joiner needs no STRV gear, or never filled the form). Sets employee to
   *  `active` + marks every open onboarding item cancelled. `details` carries
   *  the cancelled item ids so the action is reviewable. */
  ONBOARDING_DISMISS: 'onboarding.dismiss',

  // ── Offboarding ─────────────────────────────────────────────────────────
  /** Per-device decision: `details = { deviceId, from, to }`. */
  OFFBOARDING_DEVICE: 'offboarding.device_decision',
  /** Per-asset (phone tariff) decision: `details = { bambooAssetId, from, to }`. */
  OFFBOARDING_ASSET:  'offboarding.asset_decision',
  /** CSV export of "ready for Finance" lines. Placeholder — the export
   *  itself is future work. When it ships, the emit goes there. */
  OFFBOARDING_FINANCE_EXPORT: 'offboarding.finance_export',
  /** Admin closed an offboarding without going through the per-device
   *  workflow ("handled out-of-band"). Sets employee to `done` + marks
   *  every pending device decision as cancelled. `details` carries the
   *  device ids that were force-cancelled so the action can be reviewed
   *  or partially undone if needed. */
  OFFBOARDING_DISMISS: 'offboarding.dismiss',

  // ── Device admin ────────────────────────────────────────────────────────
  /** Handover recorded — device transitioned holder or returned. */
  DEVICE_HANDOVER:      'device.handover',
  /** Gear-owned peripheral added via the Devices index. */
  DEVICE_CREATE:        'device.create',
  /** Status field changed via the device-detail page. */
  DEVICE_STATUS_CHANGE: 'device.status_change',

  // ── Pending device actions (Gear → Slack → Abra reconciliation) ─────────
  /** Admin submitted a device action via the new "do everything in Gear"
   *  flow. Creates a /pending_changes doc, pings Slack, waits for Abra to
   *  reflect the change on the next sync. `details.action` carries the
   *  specific verb (handover / takeback / transfer / make_testing / sell /
   *  dispose). */
  DEVICE_PENDING_SUBMIT:     'device.pending.submit',
  /** The next sync after submit detected Abra now matches the expected
   *  state — the pending row gets archived shortly after. */
  DEVICE_PENDING_RECONCILED: 'device.pending.reconciled',
  /** Admin cancelled a pending change (mistake, request was redirected
   *  out-of-band, etc.). A "Cancelled" follow-up goes to Slack so Finance
   *  doesn't keep working on it. */
  DEVICE_PENDING_CANCELLED:  'device.pending.cancelled',

  // ── Admin management ────────────────────────────────────────────────────
  /** Admin promoted via the Settings page. `details = { email }`. */
  ADMIN_ADD:    'admin.add',
  /** Admin demoted via the Settings page. `details = { email }`. */
  ADMIN_REMOVE: 'admin.remove',

  // ── System ──────────────────────────────────────────────────────────────
  /** Single row per Bamboo → Flexi → Meraki → materialise → forms pipeline.
   *  Emitted server-side from the nightly cron AND client-side from the
   *  Sync everything button (with the admin as actor for the latter). */
  SYSTEM_SYNC_RAN:      'system.sync_ran',
  /** Per-employee Bamboo customAssets refresh — the on-demand phone-tariff
   *  button on Settings. */
  SYSTEM_ASSETS_SYNC:   'system.assets_sync',
  /** Admin edited runtime config from Settings (currently only the resale
   *  formula). `details.path` identifies which config doc + sub-key changed
   *  and `details.before` / `details.after` carry the values. */
  SYSTEM_CONFIG_CHANGE: 'system.config_change',
});

/** Allowed action values — used by the Firestore rule's shape check and
 *  the audit-log page's filter dropdown. */
export const AUDIT_ACTION_VALUES = new Set(Object.values(AUDIT_ACTION));
